/**
 * multivariant-add-to-cart.js
 *
 * Custom element <multivariant-atc>
 * - reads per-variant qty inputs
 * - POSTs /cart/add.js with items[] array
 * - dispatches CartAddEvent so the theme cart drawer opens
 * - updates optional running-total display
 */

import { CartAddEvent } from '@theme/events';

/** @typedef {{ getPricingForVariant: Function, calculatePricingForVariant: Function }} SparkInstance */
/** @type {{ spark?: SparkInstance, Shopify?: { money_format?: string }, Theme?: { moneyFormat?: string, translations?: Record<string,string> } }} */
const win = /** @type {any} */ (window);

class MultivariantATC extends HTMLElement {
  /** @type {AbortController | null} */
  #ac = null;

  /** @type {HTMLButtonElement|null} */
  #btn = null;

  /** @type {HTMLElement|null} */
  #errorEl = null;

  /** @type {HTMLElement|null} */
  #errorText = null;

  /** @type {HTMLElement|null} */
  #totalEl = null;

  /** @type {HTMLElement|null} */
  #liveEl = null;

  /** @type {ReturnType<typeof setTimeout>|undefined} */
  #errorTimer;

  /** @type {Map<string, number>} sku -> unitPriceMajor */
  #sparkPriceCache = new Map();

  connectedCallback() {
    // Always create a fresh AbortController on (re)connect. The element may be
    // moved between containers (e.g. when morphed into the quick-add modal),
    // which triggers disconnect → connect cycles. Reusing an aborted signal
    // would silently drop every listener.
    this.#ac = new AbortController();
    const sig = this.#ac.signal;

    this.#btn      = this.querySelector('[data-submit]');
    this.#errorEl  = this.querySelector('[data-error]');
    this.#errorText = this.querySelector('[data-error-text]');
    this.#totalEl  = this.querySelector('[data-total]');
    this.#liveEl   = this.querySelector('[data-live-region]');

    this.addEventListener('click',  this.#onClick,  { signal: sig });
    this.addEventListener('input',  this.#onInput,  { signal: sig });
    this.addEventListener('change', this.#onInput,  { signal: sig });
    this.#btn?.addEventListener('click', this.#onSubmit, { signal: sig });

    this.#syncUI();
    this.#initSparkPrices();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
    clearTimeout(this.#errorTimer);
  }

  // ── helpers ────────────────────────────────────────────────────────────────

  /** All enabled inputs with qty > 0 */
  #activeItems() {
    return /** @type {HTMLInputElement[]} */ (
      Array.from(this.querySelectorAll('.multivariant-atc__qty-input:not(:disabled)'))
    )
      .map((el) => ({
        input:     el,
        variantId: el.dataset.variantId ?? '',
        qty:       Math.max(0, parseInt(el.value, 10) || 0),
        priceCents: this.#majorToCents(el.dataset.priceMajor ?? '0'),
      }))
      .filter((i) => i.qty > 0);
  }

  /**
   * Converts a major-unit price string (e.g. "14.00" or "14,00" or "€14.00")
   * into integer cents for Shopify's {{ amount }} formatting.
   * @param {string} raw
   * @returns {number}
   */
  #majorToCents(raw) {
    const cleaned = (raw ?? '0')
      .toString()
      .replace(/\s/g, '')
      .replace(/[^\d.,-]/g, '');

    if (!cleaned) return 0;

    const hasComma = cleaned.includes(',');
    const hasDot = cleaned.includes('.');

    let num;
    if (hasComma && !hasDot) {
      num = parseFloat(cleaned.replace(',', '.'));
    } else {
      // Remove thousands separators if they exist
      num = parseFloat(cleaned.replace(/,/g, ''));
    }

    if (Number.isNaN(num)) return 0;
    return Math.round(num * 100);
  }

  /** Clamp input to its step/min/max rules */
  #clamp(/** @type {HTMLInputElement} */ el) {
    let v    = parseInt(el.value, 10) || 0;
    const step = parseInt(el.step, 10)              || 1;
    const min  = parseInt(el.dataset.min ?? '1', 10) || 1;
    const max  = el.dataset.max ? parseInt(el.dataset.max, 10) : Infinity;

    if (v < 0) v = 0;
    if (v > 0 && v < min) v = min;
    if (v > max) v = max;
    if (v > 0)  v = Math.round(v / step) * step;

    el.value = String(v);
  }

  /** Enable/disable submit + update total */
  #syncUI() {
    if (this.#btn) this.#btn.disabled = this.#activeItems().length === 0;
    this.#updateTotal();
  }

  #updateTotal() {
    if (!this.#totalEl) return;
    const sum = this.#activeItems().reduce((acc, i) => acc + i.qty * i.priceCents, 0);
    this.#totalEl.textContent = sum > 0 ? this.#money(sum) : '';
  }

  /**
   * Wait for window.spark to be ready, then fill all B2B price placeholders.
   * Uses spark.getPricingForVariant(parentId, sku) which returns { price, currencyCode, ... }
   * where price is already in major units (e.g. 14.00).
   */
  async #initSparkPrices() {
    const placeholders = Array.from(this.querySelectorAll('.multivariant-atc__spark-price'));
    if (!placeholders.length) return;

    const parentId = this.dataset.productId;
    if (!parentId) return;

    // spark.getPricingForVariant is the correct API per docs.
    // It may not exist yet if Spark loads async — wait up to 3s.
    const spark = await this.#waitForSpark();
    if (!spark) return;

    console.log('[multivariant-atc] Spark ready. parentId =', parentId, '| SKUs:', placeholders.map((el) => /** @type {HTMLElement} */ (el).dataset?.sparkSku));

    for (const el of placeholders) {
      if (!(el instanceof HTMLElement)) continue;
      const sku = el.dataset.sparkSku ?? '';
      if (!sku) continue;

      try {
        // @ts-ignore — Spark JS SDK
        const pricing = await spark.getPricingForVariant(String(parentId), String(sku));
        console.log('[multivariant-atc] getPricingForVariant SKU', sku, '→', pricing);

        // pricing = { price: number|null, rrp: number|null, priceBreaks: [], currencyCode: string }
        const priceMajor = Number(pricing?.price ?? 0);

        if (!Number.isFinite(priceMajor) || priceMajor <= 0) {
          el.textContent = '—';
          continue;
        }

        // Cache and display
        this.#sparkPriceCache.set(sku, priceMajor);
        el.textContent = this.#money(Math.round(priceMajor * 100));

        // Seed the qty input so running total uses B2B price
        const row = el.closest('[data-variant-id]');
        const qtyInput = row?.querySelector('.multivariant-atc__qty-input');
        if (qtyInput instanceof HTMLInputElement) {
          qtyInput.dataset.priceMajor = String(priceMajor);
        }
      } catch (err) {
        console.error('[multivariant-atc] getPricingForVariant failed for SKU', sku, err);
        el.textContent = '—';
      }
    }
  }

  /**
   * On qty change, recalculate using calculatePricingForVariant(parentId, sku, qty)
   * which accounts for tier/break pricing at specific quantities.
   * @param {HTMLInputElement} input
   */
  async #refreshSparkPricingForInput(input) {
    const row = input.closest('[data-variant-id]');
    const placeholder = row?.querySelector('.multivariant-atc__spark-price');
    if (!placeholder || !(placeholder instanceof HTMLElement)) return;

    const sku = input.dataset.sparkSku ?? placeholder.dataset.sparkSku ?? '';
    if (!sku) return;

    const qty = Math.max(0, parseInt(input.value, 10) || 0);
    const parentId = this.dataset.productId;
    if (!parentId) return;

    // If qty is 0, fall back to the cached base price
    if (qty <= 0) {
      const cached = this.#sparkPriceCache.get(sku);
      if (cached) input.dataset.priceMajor = String(cached);
      return;
    }

    const spark = win.spark;
    if (typeof spark?.calculatePricingForVariant !== 'function') return;

    try {
      // calculatePricingForVariant(parentId, sku, qty) -> { totalPrice, unitPrice, currencyCode }
      // @ts-ignore
      const pricing = await spark.calculatePricingForVariant(String(parentId), String(sku), qty);
      const unitPriceMajor = Number(pricing?.unitPrice ?? 0);

      if (!Number.isFinite(unitPriceMajor) || unitPriceMajor <= 0) return;

      // Update visible price to show unit price at this qty break
      placeholder.textContent = this.#money(Math.round(unitPriceMajor * 100));
      // Update input so totals are correct
      input.dataset.priceMajor = String(unitPriceMajor);
    } catch (err) {
      console.error('[multivariant-atc] calculatePricingForVariant failed', err);
    }
  }

  /**
   * Wait for window.spark to expose getPricingForVariant, up to 3 seconds.
   * SparkLayer fires a custom "onReady" callback but we can't hook into it here,
   * so we poll with exponential backoff.
   * @returns {Promise<SparkInstance | null>}
   */
  async #waitForSpark() {
    // Already ready
    if (typeof win.spark?.getPricingForVariant === 'function') return win.spark;

    const delays = [100, 200, 300, 500, 800, 1000];
    for (const delay of delays) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => window.setTimeout(r, delay));
      if (typeof win.spark?.getPricingForVariant === 'function') return win.spark;
    }
    console.warn('[multivariant-atc] window.spark not ready after 3s — Spark prices not loaded');
    return null;
  }

  /** Format cents using Shopify's money_format if available */
  #money(/** @type {number} */ cents) {
    try {
      const fmt =
        this.dataset.moneyFormat ||
        win.Shopify?.money_format ||
        win.Theme?.moneyFormat ||
        '&euro;{{amount}}';
      const amount = (cents / 100).toFixed(2);
      return fmt
        .replace(/\{\{\s*amount\s*\}\}/g,            amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','));
    } catch {
      return `€${(cents / 100).toFixed(2)}`;
    }
  }

  #showError(/** @type {string} */ msg) {
    if (this.#errorEl) {
      this.#errorEl.classList.remove('hidden');
      if (this.#errorText) this.#errorText.textContent = msg;
    }
    clearTimeout(this.#errorTimer);
    this.#errorTimer = setTimeout(() => {
      this.#errorEl?.classList.add('hidden');
    }, 8000);
  }

  #clearError() {
    this.#errorEl?.classList.add('hidden');
    if (this.#errorText) this.#errorText.textContent = '';
    clearTimeout(this.#errorTimer);
  }

  #setLoading(/** @type {boolean} */ on) {
    if (!this.#btn) return;
    if (on) {
      this.#btn.setAttribute('data-loading', '');
      this.#btn.disabled = true;
    } else {
      this.#btn.removeAttribute('data-loading');
      this.#syncUI();
    }
  }

  #announce(/** @type {string} */ text) {
    if (!this.#liveEl) return;
    this.#liveEl.textContent = '';
    // small tick so screen readers re-read the update
    requestAnimationFrame(() => { if (this.#liveEl) this.#liveEl.textContent = text; });
  }

  // ── event handlers ─────────────────────────────────────────────────────────

  #onClick = async (/** @type {MouseEvent} */ e) => {
    const btn = /** @type {HTMLElement|null} */ (/** @type {HTMLElement} */ (e.target).closest('.multivariant-atc__qty-btn'));
    if (!btn || /** @type {HTMLButtonElement} */ (btn).disabled) return;

    const row   = btn.closest('[data-variant-id]');
    const input = /** @type {HTMLInputElement|null} */ (row?.querySelector('.multivariant-atc__qty-input'));
    if (!input || input.disabled) return;

    const step  = parseInt(input.step, 10) || 1;
    const min   = parseInt(input.dataset.min ?? '1', 10);
    let   val   = parseInt(input.value, 10) || 0;

    if (btn.dataset.action === 'plus') {
      val = val === 0 ? min : val + step;
    } else {
      val = val <= min ? 0 : val - step;
    }

    input.value = String(val);
    this.#clamp(input);
    await this.#refreshSparkPricingForInput(input);
    this.#syncUI();
  };

  #onInput = async (/** @type {Event} */ e) => {
    const input = /** @type {HTMLInputElement|null} */ (
      /** @type {HTMLElement} */ (e.target).closest('.multivariant-atc__qty-input')
    );
    if (!input) return;
    this.#clamp(input);
    await this.#refreshSparkPricingForInput(input);
    this.#syncUI();
  };

  #onSubmit = async () => {
    const items = this.#activeItems();
    if (!items.length) return;

    this.#clearError();
    this.#setLoading(true);

    try {
      const payload = {
        items: items.map(({ variantId, qty }) => ({
          id:       Number(variantId),
          quantity: qty,
        })),
      };

      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept:         'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });

      const responseText = await res.text();
      /** @type {any} */
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseErr) {
        console.error('[multivariant-atc] Failed to parse JSON response:', responseText);
        this.#showError('Unable to add items to cart. Please try again.');
        return;
      }

      if (!res.ok || data.status) {
        this.#showError(data.description ?? data.message ?? 'Unable to add items to cart.');
        return;
      }

      // Announce success to screen readers
      const totalQty   = items.reduce((s, i) => s + i.qty, 0);
      const addedText = win.Theme?.translations?.added ?? 'Added';
      this.#announce(addedText);

      // Fire CartAddEvent — the cart drawer listens for this
      this.dispatchEvent(
        new CartAddEvent(
          data,
          String(items[0]?.variantId ?? ''),
          {
            source:    'multivariant-atc',
            itemCount: totalQty,
            productId: this.dataset.productId,
          }
        )
      );

      // Reset all inputs
      this.querySelectorAll('.multivariant-atc__qty-input').forEach(
        (el) => { if (el instanceof HTMLInputElement) el.value = '0'; }
      );
      this.#syncUI();

    } catch (err) {
      console.error('[multivariant-atc]', err);
      this.#showError('Something went wrong. Please try again.');
    } finally {
      this.#setLoading(false);
    }
  };
}

if (!customElements.get('multivariant-atc')) {
  customElements.define('multivariant-atc', MultivariantATC);
}
